import {
    AddCouponCodeToOrderResponse,
    AddressFormData,
    GetOrderPricesResponse,
    GetOrderResponse,
    PaymentMethodsResponse,
    StartPaymentFormData,
    StartPaymentResponse,
    syliusCustomerFetch,
    syliusFetch,
    UpdateOrderResponse,
} from '../../../entities/@api/Sylius';
import { CheckoutFormData } from '../../../entities/@forms/Checkout/Checkout';
import { CouponCodeFormData } from '../../../entities/@forms/CouponCodeForm/CouponCodeForm';
import { transformToCart } from '../../../entities/@shop/Cart/CartTransformers';
import { OrderInterface } from '../../../entities/@shop/Order/Order';
import { transformToPaymentMethods } from '../../../entities/@shop/PaymentMethod/PaymentMethodTransformers';
import { isFetchResultSuccessful } from '../../../entities/FetchResult/FetchResult';
import { calculateDiscounts } from '../../../entities/Price/PriceTransformers';
import trans from '../../../helpers/trans';
import { ReduxFetchAction } from '../../defaults';
import { addNegativeToast, addPositiveToast } from '../../toast/toastActions';
import { setCart } from '../cart/cartSlice';
import { setError, setIsLoading, setOrder } from './orderSlice';

export const updateOrder: ReduxFetchAction<CheckoutFormData> = formData => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { orderSlice, customerSlice } = getState();
        const { order } = orderSlice;
        const { customer } = customerSlice;

        const shippingAddress: AddressFormData = {
            label: formData.shippingAddress.label,
            firstName: formData.firstName, // TODO: Use firstName from Address, if customers can add names to addresses
            lastName: formData.lastName, // TODO: Use lastName from Address, if customers can add names to addresses
            phoneNumber: formData.phoneNumber,
            street: formData.shippingAddress.street,
            number: formData.shippingAddress.houseNumber,
            addition: formData.shippingAddress.addition,
            city: formData.shippingAddress.city,
            postcode: formData.shippingAddress.postalCode.toUpperCase(),
            countryCode: formData.shippingAddress.country,
        };

        const billingAddress: AddressFormData = {
            label: formData.billingAddress.label,
            firstName: formData.firstName, // TODO: Use firstName from Address, if customers can add names to addresses
            lastName: formData.lastName, // TODO: Use lastName from Address, if customers can add names to addresses
            phoneNumber: formData.phoneNumber,
            street: formData.billingAddress.street,
            number: formData.billingAddress.houseNumber,
            addition: formData.billingAddress.addition,
            city: formData.billingAddress.city,
            postcode: formData.billingAddress.postalCode.toUpperCase(),
            countryCode: formData.billingAddress.country,
        };

        const infoBody = {
            email: formData.email,
            shippingAddress,
            billingAddress,
        };

        // Connects guest to cart and automatically creates a customer in Sylius
        const addInfoToOrderResponse = await syliusCustomerFetch<UpdateOrderResponse>(`/shop/orders/${order.id}`, {
            customer,
            method: 'PUT',
            body: JSON.stringify(infoBody),
        });

        if (!isFetchResultSuccessful(addInfoToOrderResponse)) {
            dispatch(setError(addInfoToOrderResponse.error));
            return;
        }

        const pricesResponse = await syliusFetch<GetOrderPricesResponse>(`/shop/orders/${order.id}/prices`);

        if (!isFetchResultSuccessful(pricesResponse)) {
            dispatch(setError(pricesResponse.error));
            return;
        }

        const addInfoToOrderResource = addInfoToOrderResponse.data;
        const pricesResource = pricesResponse.data;

        const updatedOrder = transformToCart(addInfoToOrderResource, pricesResource);

        const newOrder: OrderInterface = {
            ...updatedOrder,
            paymentMethods: order.paymentMethods,
            items: order.items,
        };

        dispatch(setOrder(newOrder));
    } catch (error) {
        console.error('[updateOrder]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const getPaymentMethods: ReduxFetchAction = () => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { orderSlice } = getState();
        const { order } = orderSlice;

        if (!order.id) {
            return;
        }

        const paymentMethodsResponse = await syliusFetch<PaymentMethodsResponse>(`/shop/orders/${order.id}/payment_methods`);

        if (!isFetchResultSuccessful(paymentMethodsResponse)) {
            dispatch(setError(paymentMethodsResponse.error));
            return;
        }

        const paymentMethodsResource = paymentMethodsResponse.data;

        const paymentMethods = paymentMethodsResource.methods.length
            ? paymentMethodsResource.methods.map(transformToPaymentMethods)
            : undefined;

        const updatedOrder: OrderInterface = {
            ...order,
            paymentMethods,
        };

        dispatch(setOrder(updatedOrder));
    } catch (error) {
        console.error('[getPaymentMethods]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const payOrder: ReduxFetchAction<StartPaymentFormData> = formData => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { orderSlice } = getState();
        const { order } = orderSlice;

        const payOrderResponse = await syliusFetch<StartPaymentResponse>(`/shop/orders/${order.id}/start_payment`, {
            method: 'POST',
            body: JSON.stringify(formData),
        });

        if (!isFetchResultSuccessful(payOrderResponse)) {
            dispatch(setError(payOrderResponse.error));

            dispatch(addNegativeToast({
                title: trans('errors.genericTitle'),
                description: payOrderResponse.error,
            }));

            return;
        }

        const payOrderResource = payOrderResponse.data;
        const { url } = payOrderResource.payment;

        const updatedOrder: OrderInterface = {
            ...order,
            paymentEnvironmentUrl: url,
        };

        dispatch(setOrder(updatedOrder));
    } catch (error) {
        console.error('[payOrder]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

// TODO: Refactor so 'customer' and 'order' are parameters of this function, instead of a lookup
export const connectOrderToCustomer: ReduxFetchAction = () => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { orderSlice, customerSlice } = getState();
        const { order } = orderSlice;
        const { customer } = customerSlice;

        const connectOrderResponse = await syliusCustomerFetch<GetOrderResponse>(`/shop/orders/${order.id}`, {
            customer,
        });

        if (!isFetchResultSuccessful(connectOrderResponse)) {
            dispatch(setError(connectOrderResponse.error));
        }
    } catch (error) {
        console.error('[connectOrderToCustomer]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const addCouponCodeToOrder: ReduxFetchAction<CouponCodeFormData> = formData => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { orderSlice } = getState();
        const { order } = orderSlice;

        const addCouponCodeToOrderResponse = await syliusFetch<AddCouponCodeToOrderResponse>(`/shop/orders/${order.id}/discount_code`, {
            method: 'POST',
            body: JSON.stringify(formData),
        });

        if (!isFetchResultSuccessful(addCouponCodeToOrderResponse)) {
            dispatch(addNegativeToast({
                title: addCouponCodeToOrderResponse.error,
            }));

            dispatch(setError(addCouponCodeToOrderResponse.error));
            return;
        }

        const discounts = calculateDiscounts(addCouponCodeToOrderResponse.data);

        const newOrder: OrderInterface = {
            ...order,
            subTotalPrice: addCouponCodeToOrderResponse.data.price?.original || 0,
            totalPrice: addCouponCodeToOrderResponse.data.price?.stillToBePaid || 0,
            discounts,
        };

        dispatch(setCart(newOrder));
        dispatch(setOrder(newOrder));

        dispatch(addPositiveToast({
            title: trans('redux.order.addCouponCodeSuccessMessageTitle'),
        }));
    } catch (error) {
        console.error('[addCouponCodeToOrder]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const removeCouponCodeFromOrder: ReduxFetchAction = () => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { orderSlice } = getState();
        const { order } = orderSlice;

        const formData = { discountCode: null };

        const removeCouponCodeFromOrderResponse = await syliusFetch<AddCouponCodeToOrderResponse>(`/shop/orders/${order.id}/discount_code`, {
            method: 'POST',
            body: JSON.stringify(formData),
        });

        if (!isFetchResultSuccessful(removeCouponCodeFromOrderResponse)) {
            dispatch(setError(removeCouponCodeFromOrderResponse.error));
            return;
        }

        const discounts = order.discounts.filter(discount => discount.productDiscount);

        const newOrder: OrderInterface = {
            ...order,
            discounts,
        };

        dispatch(setCart(newOrder));
        dispatch(setOrder(newOrder));
    } catch (error) {
        console.error('[removeCouponCodeFromOrder]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};
